// src/components/GoToPremiumMobile.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";
import TimelineIcon from "@mui/icons-material/Timeline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";

import MobileHeader from "./headers/MobileHearder";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

// Stripe payments SDK
import { getApp } from "firebase/app";
import {
  getStripePayments,
  getCurrentUserSubscriptions,
  createCheckoutSession,
  getProducts,
} from "@invertase/firestore-stripe-payments";

const app = getApp();
const payments = getStripePayments(app, {
  productsCollection: "products",
  customersCollection: "customers",
});

const heroIcons = [
  AutoAwesomeIcon,
  RocketLaunchIcon,
  QueryStatsIcon,
  SupportAgentIcon,
  SecurityIcon,
  TimelineIcon,
];

const priceId = "price_1ShRghKxPduZsYMS7HitXy40";
const salesEmail = "hello@talk-mate.com";

export default function GoToPremiumMobile() {
  const theme = useTheme();
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();

  const [, setRole] = useState("Normal User");
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    getProducts(payments, { includePrices: true, activeOnly: true })
      .then((prods) => {
        console.log("[Premium][Mobile] products loaded", {
          count: prods?.length,
          ids: prods?.map((p) => p.id),
          prices: prods?.flatMap((p) => p.prices?.map((pr) => pr.id)),
        });
      })
      .catch((err) => console.error("[Premium][Mobile] getProducts error", err));
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, "profilsUsers", currentUser.uid))
      .then(
        (snap) => snap.exists() && setRole(snap.data().role || "Normal User")
      )
      .catch(() => setRole("Normal User"));
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setSubscriptions([]);
      setSubsLoading(false);
      return () => {};
    }

    const fetchSubs = async () => {
      try {
        const subs = await getCurrentUserSubscriptions(payments, {
          status: "active",
        });
        if (!cancelled) setSubscriptions(subs);
      } catch (err) {
        console.error("Failed to load subscriptions", err);
        if (!cancelled) setSubscriptions([]);
      } finally {
        if (!cancelled) setSubsLoading(false);
      }
    };

    fetchSubs();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleSubscribe = async () => {
    console.log("[Premium][Mobile] checkout start", {
      priceId,
      hasActive,
      subsLoading,
      checkoutLoading,
    });
    setCheckoutLoading(true);
    try {
      const session = await createCheckoutSession(payments, {
        price: priceId,
        success_url: `${window.location.origin}/app/premium/success`,
        cancel_url: `${window.location.origin}/app/premium/cancel`,
      });
      console.log("[Premium][Mobile] session created", session);
      if (session?.url) {
        window.location.assign(session.url);
      } else {
        console.error("[Premium][Mobile] missing session url", session);
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error("[Premium][Mobile] checkout error", err);
      setCheckoutLoading(false);
    }
  };

  const hasActive = subscriptions.length > 0;

  const heroStats = useMemo(
    () => [
      {
        value: translate("PremiumStat1Value"),
        label: translate("PremiumStat1Label"),
      },
      {
        value: translate("PremiumStat2Value"),
        label: translate("PremiumStat2Label"),
      },
      {
        value: translate("PremiumStat3Value"),
        label: translate("PremiumStat3Label"),
      },
    ],
    [translate]
  );

  const featureCards = useMemo(
    () => [
      {
        title: translate("PremiumFeature1Title"),
        description: translate("PremiumFeature1Desc"),
      },
      {
        title: translate("PremiumFeature4Title"),
        description: translate("PremiumFeature4Desc"),
      },
    ],
    [translate]
  );

  const planItems = useMemo(
    () =>
      [
        translate("PremiumPlanIncluded1"),
        translate("PremiumPlanIncluded2"),
        translate("PremiumPlanIncluded3"),
        translate("PremiumPlanIncluded4"),
        translate("PremiumPlanIncluded5"),
        translate("PremiumPlanIncluded6"),
      ].filter(Boolean),
    [translate]
  );

  const workflowSteps = useMemo(
    () => [
      {
        title: translate("PremiumWorkflow1Title"),
        description: translate("PremiumWorkflow1Desc"),
      },
      {
        title: translate("PremiumWorkflow2Title"),
        description: translate("PremiumWorkflow2Desc"),
      },
      {
        title: translate("PremiumWorkflow3Title"),
        description: translate("PremiumWorkflow3Desc"),
      },
    ],
    [translate]
  );

  const gradientBg =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #0d47a1 0%, #1a237e 50%, #1b5e20 100%)"
      : "linear-gradient(135deg, #eef2ff 0%, #e0f2ff 40%, #f1f8e9 100%)";

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <MobileHeader
          title={translate("PremiumPageTitle")}
          showProfileItem={false}
        />
      </Box>

      <Container maxWidth="md" sx={{ pt: 3, pb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 3, sm: 4 },
            mb: 4,
            backgroundImage: gradientBg,
            color: theme.palette.mode === "dark" ? "#fff" : "inherit",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.12)"
                : "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <Stack spacing={2.5}>
            <Chip
              label={translate("PremiumBadge")}
              color="primary"
              variant={theme.palette.mode === "dark" ? "filled" : "outlined"}
            />
            <Typography variant="h4" fontWeight={700}>
              {translate("PremiumHeroTitle")}
            </Typography>
            <Typography variant="body1">
              {translate("PremiumHeroSubtitle")}
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disableElevation
                onClick={() => !hasActive && handleSubscribe()}
                disabled={checkoutLoading || subsLoading || hasActive}
                sx={{ textTransform: "none" }}
              >
                {hasActive
                  ? translate("AlreadyPremium")
                  : translate("PremiumHeroCTA")}
              </Button>
            </Stack>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {heroStats.map((stat, index) => {
                const Icon = heroIcons[index] || AutoAwesomeIcon;
                return (
                  <Grid key={stat.label} item xs={12} sm={4}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.12)"
                              : "rgba(15,23,42,0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2">{stat.label}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Paper>

        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" color="text.secondary">
            {translate("PremiumFeaturesEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {translate("PremiumFeaturesTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {translate("PremiumFeaturesSubtitle")}
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {featureCards.map((feature, index) => {
            const Icon = heroIcons[index] || AutoAwesomeIcon;
            return (
              <Grid key={feature.title} item xs={12} sm={6}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    height: "100%",
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(15,23,42,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(15,23,42,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                  <Typography variant="h6">{feature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Stack spacing={3} sx={{ mt: 5 }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(15,23,42,0.08)",
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {translate("PremiumPlanCardTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate("PremiumPlanCardSubtitle")}
            </Typography>
            <Typography variant="h3" color="primary" sx={{ mb: 2 }}>
              {translate("PremiumPlanPrice")}
              <Typography component="span" fontSize="1rem">
                {translate("PremiumPlanPriceSuffix")}
              </Typography>
            </Typography>
            <List dense>
              {planItems.map((item) => (
                <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                disableElevation
                onClick={() => !hasActive && handleSubscribe()}
                disabled={checkoutLoading || subsLoading || hasActive}
                sx={{ textTransform: "none" }}
              >
                {hasActive
                  ? translate("AlreadyPremium")
                  : translate("PremiumHeroCTA")}
              </Button>
              <Button
                variant="outlined"
                component="a"
                href="https://mail.google.com/mail/?view=cm&fs=1&to=arnaud@5ideasproduction.com"
                target="_blank"
                rel="noreferrer"
                fullWidth
                sx={{ textTransform: "none" }}
              >
                {translate("ContactUs")}
              </Button>
            </Stack>
          </Paper>

          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px dashed",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(15,23,42,0.2)",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(15,23,42,0.02)",
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {translate("PremiumWorkflowTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate("PremiumWorkflowSubtitle")}
            </Typography>
            <Stack spacing={2.5}>
              {workflowSteps.map((step, index) => (
                <Box key={step.title}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    fontWeight={600}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Typography variant="h6">{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
